import React from 'react';
import {
  Box,
  Chip,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link, useLocation } from 'react-router-dom';
import BusinessIcon from '@mui/icons-material/Business';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import PeopleIcon from '@mui/icons-material/People';
import HistoryIcon from '@mui/icons-material/History';
import KingBedIcon from '@mui/icons-material/KingBed';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import HotelIcon from '@mui/icons-material/Hotel';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined';
import { useAuth } from '../../contexts/AuthContext.tsx';
import { topBarHeights } from '../Layout/layoutConstants.ts';

const drawerWidth = 252;

interface NavItem {
  label: string;
  icon: React.ReactElement;
  path: string;
  permission?: string;
  roles?: string[];
}

const navItems: NavItem[] = [
  {
    label: 'Overview',
    icon: <SpaceDashboardIcon />,
    path: '/dashboard',
    permission: 'support.web.dashboard:VIEW',
  },
  {
    label: 'Tenants',
    icon: <BusinessIcon />,
    path: '/dashboard/tenants',
    permission: 'support.web.tenants:VIEW',
  },
  {
    label: 'Locations',
    icon: <LocationCityIcon />,
    path: '/dashboard/locations',
    permission: 'support.web.locations:VIEW',
  },
  {
    label: 'Spaces',
    icon: <ViewModuleIcon />,
    path: '/dashboard/spaces',
    roles: ['SUPER_ADMIN'],
  },
  {
    label: 'Tables',
    icon: <TableRestaurantIcon />,
    path: '/dashboard/tables',
    roles: ['SUPER_ADMIN'],
  },
  {
    label: 'Reservations',
    icon: <EventSeatIcon />,
    path: '/dashboard/reservations',
    permission: 'support.web.reservations:VIEW',
  },
  {
    label: 'Users',
    icon: <PeopleIcon />,
    path: '/dashboard/users',
    permission: 'support.web.users:VIEW',
  },
  {
    label: 'Access Directory',
    icon: <AdminPanelSettingsIcon />,
    path: '/dashboard/access-directory',
    permission: 'support.web.access_directory:VIEW',
  },
  {
    label: 'Location Team',
    icon: <GroupsOutlinedIcon />,
    path: '/dashboard/location-team',
    permission: 'support.web.location_team:VIEW',
  },
  {
    label: 'Permission Profiles',
    icon: <ShieldOutlinedIcon />,
    path: '/dashboard/permission-profiles',
    permission: 'support.web.permission_profiles:VIEW',
  },
  {
    label: 'Support Actions',
    icon: <FactCheckOutlinedIcon />,
    path: '/dashboard/support-actions',
    permission: 'support.web.support_actions:VIEW',
  },
  {
    label: 'Logs',
    icon: <HistoryIcon />,
    path: '/dashboard/logs',
    permission: 'support.web.logs:VIEW',
  },
  {
    label: 'Hotels',
    icon: <HotelIcon />,
    path: '/dashboard/hotels',
    roles: ['SUPER_ADMIN'],
  },
  {
    label: 'Hotel Rooms',
    icon: <KingBedIcon />,
    path: '/dashboard/hotel-rooms',
    roles: ['SUPER_ADMIN'],
  },
  {
    label: 'Housekeeping',
    icon: <CleaningServicesIcon />,
    path: '/dashboard/hotel-housekeeping',
    roles: ['SUPER_ADMIN'],
  },
  {
    label: 'Hotel Room Types',
    icon: <CategoryOutlinedIcon />,
    path: '/dashboard/hotel-room-types',
    roles: ['SUPER_ADMIN'],
  },
  {
    label: 'Shifts Monitor',
    icon: <ScheduleOutlinedIcon />,
    path: '/dashboard/shifts-monitor',
    roles: ['SUPER_ADMIN'],
  },
  {
    label: 'Reports',
    icon: <AssessmentOutlinedIcon />,
    path: '/dashboard/reports',
    roles: ['SUPER_ADMIN'],
  },
  {
    label: 'Inventory Overview',
    icon: <Inventory2OutlinedIcon />,
    path: '/dashboard/inventory-overview',
    roles: ['SUPER_ADMIN'],
  },
  {
    label: 'Inventory Items',
    icon: <WidgetsOutlinedIcon />,
    path: '/dashboard/inventory/items',
    roles: ['SUPER_ADMIN'],
  },
  {
    label: 'Inventory Batches',
    icon: <LayersOutlinedIcon />,
    path: '/dashboard/inventory/batches',
    roles: ['SUPER_ADMIN'],
  },
  {
    label: 'Inventory Audit',
    icon: <FactCheckOutlinedIcon />,
    path: '/dashboard/inventory-audit',
    roles: ['SUPER_ADMIN'],
  },
  {
    label: 'Demo Tenant Matrix',
    icon: <ScienceOutlinedIcon />,
    path: '/dashboard/demo-matrix',
    roles: ['SUPER_ADMIN'],
  },
  {
    label: 'Feature Flags',
    icon: <FlagOutlinedIcon />,
    path: '/dashboard/feature-flags',
    roles: ['SUPER_ADMIN'],
  },
  {
    label: 'Fiscal Health',
    icon: <ReceiptLongOutlinedIcon />,
    path: '/dashboard/fiscal-health',
    roles: ['SUPER_ADMIN'],
  },
  {
    label: 'Offline Queues',
    icon: <CloudSyncOutlinedIcon />,
    path: '/dashboard/offline-queues',
    roles: ['SUPER_ADMIN'],
  },
];

const Sidebar: React.FC = () => {
  const location = useLocation();
  const { user, hasMenuPermission } = useAuth();
  const normalizedRoles = (user?.roles || []).map((role) =>
    role.toUpperCase().replace(/^ROLE_/, ''),
  );

  const visibleItems = navItems.filter((item) => {
    if (item.roles?.length) {
      return item.roles.some((role) => normalizedRoles.includes(role.toUpperCase()));
    }
    if (item.permission) {
      const separator = item.permission.lastIndexOf(':');
      if (separator > 0 && separator < item.permission.length - 1) {
        return hasMenuPermission(
          item.permission.slice(0, separator),
          item.permission.slice(separator + 1),
        );
      }
      return hasMenuPermission(item.permission);
    }
    return true;
  });

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: drawerWidth,
          boxSizing: 'border-box',
        },
      }}
    >
      <Box sx={{ height: topBarHeights, flexShrink: 0 }} />
      <Box sx={{ px: 2.5, py: 2 }}>
        <Typography
          variant="overline"
          sx={{ color: 'text.secondary', letterSpacing: '0.18em' }}
        >
          Internal 3100
        </Typography>
        <Typography variant="h6" sx={{ mt: 0.5 }}>
          Internal Console
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Direct support operations and global visibility for internal staff.
        </Typography>
        <Chip
          label={user?.roles?.join(', ') || 'Internal access'}
          size="small"
          color="primary"
          sx={{ mt: 1.5 }}
        />
      </Box>
      <Divider />
      <List sx={{ px: 1.25, py: 1.5 }}>
        {visibleItems.map((item) => {
          const selected =
            item.path === '/dashboard'
              ? location.pathname === item.path
              : location.pathname.startsWith(item.path);

          return (
            <ListItemButton
              key={item.path}
              component={Link}
              to={item.path}
              selected={selected}
              sx={{
                minHeight: 46,
                borderRadius: 3,
                mb: 0.5,
                '&.Mui-selected': {
                  bgcolor: alpha('#172033', 0.08),
                  color: 'primary.main',
                },
                '&.Mui-selected:hover': {
                  bgcolor: alpha('#172033', 0.12),
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 38,
                  color: selected ? 'primary.main' : 'text.secondary',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: 14,
                  fontWeight: selected ? 700 : 600,
                }}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Drawer>
  );
};

export default Sidebar;
export { drawerWidth };
